/**
 * F28 — Email Notifications (Postmark) shared types + Zod schemas.
 *
 * Surfaces:
 *   - EmailSuppressionReason / EmailSuppressionSource: the 5-value enum
 *     persisted on `email_suppressions.source` (Decision D9)
 *   - EmailSuppression: read shape returned by /admin/email-suppressions
 *   - EmailUnsubscribeToken: read shape returned by token introspection
 *     (admin support tooling only — not exposed to users)
 *   - PostmarkBounceWebhookPayload / PostmarkComplaintWebhookPayload: the
 *     Zod-validated webhook bodies + their TS types (Decision D13)
 *   - EmailRenderResult: `{ subject, html, text }` returned by @bravemove/email's
 *     renderer (Decision D11 — html-to-text auto-generates text)
 *   - EmailTemplateName: closed enum of the 10 templates F28 ships + the
 *     data-driven `generic` branded letter (the AdminEmailService /
 *     UserEmailService plain-text→branded-HTML send seam)
 *   - EmailErrorCode: 10 codes used across the EmailChannel + routes + webhook
 *   - Input schemas: unsubscribe-feedback POST + admin-suppression CRUD
 *
 * Cross-references:
 *   - F26 NotificationChannel ('email' is one of its values)
 *   - F26 NotificationCategory (the 9-value union including monthly_digest +
 *     newsletter F28 added in migration 0020)
 *   - apps/api/src/services/notification-channels/email.ts consumes these
 */

import { z } from 'zod';

// ─── Suppression list (Decision D9 + D18) ───────────────────────────────────

export const EMAIL_SUPPRESSION_SOURCES = [
  'hard_bounce',
  'soft_bounce_exhausted',
  'spam_complaint',
  'admin_override',
  'user_unsubscribe_required',
] as const;

export type EmailSuppressionSource = (typeof EMAIL_SUPPRESSION_SOURCES)[number];

/**
 * Resend webhook delivery (`POST /webhooks/resend`, founder decision
 * 2026-10-27: production email stays on Resend). Only the fields the handler
 * reads are named; Resend adds more (broadcast_id, tags, …), so unknown keys
 * pass through. `to` is always an array, even for one recipient.
 */
export const ResendWebhookPayloadSchema = z
  .object({
    type: z.string().min(1),
    created_at: z.string().optional(),
    data: z
      .object({
        email_id: z.string().optional(),
        to: z.array(z.string().email()).default([]),
        subject: z.string().optional(),
        bounce: z
          .object({
            type: z.string().optional(),
            subType: z.string().optional(),
            message: z.string().optional(),
          })
          .passthrough()
          .optional(),
      })
      .passthrough(),
  })
  .passthrough();
export type ResendWebhookPayload = z.infer<typeof ResendWebhookPayloadSchema>;

/** Alias for backward-compat with the F28 plan doc which uses "reason". */
export type EmailSuppressionReason = EmailSuppressionSource;

export const EmailSuppressionSourceSchema = z.enum(EMAIL_SUPPRESSION_SOURCES);

export interface EmailSuppression {
  readonly emailAddress: string;
  readonly reason: string;
  readonly source: EmailSuppressionSource;
  readonly suppressedAt: string; // ISO 8601
  readonly notes: string | null;
  readonly addedByAdminId: string | null;
  readonly postmarkMessageId: string | null;
  readonly countryCode: string;
}

// ─── Unsubscribe token (Decision D17) ───────────────────────────────────────

export interface EmailUnsubscribeToken {
  readonly id: string;
  readonly userId: string;
  readonly category: string;
  readonly channel: 'email';
  readonly signatureHash: string;
  readonly issuedAt: string;
  readonly consumedAt: string | null;
  readonly expiresAt: string;
  readonly sourceEmailMessageId: string | null;
  readonly countryCode: string;
}

// ─── Postmark webhook payloads (Decision D13) ───────────────────────────────
//
// Bounce + complaint webhook shapes per Postmark's developer docs
// (https://postmarkapp.com/developer/webhooks). We Zod-validate at the
// route handler before any side effect.

export const PostmarkBounceWebhookPayloadSchema = z.object({
  RecordType: z.literal('Bounce'),
  ID: z.number().int(),
  Type: z.string(), // 'HardBounce' | 'SoftBounce' | 'Transient' | etc. (Postmark has ~30 types)
  TypeCode: z.number().int(),
  Name: z.string(),
  Tag: z.string().optional().default(''),
  MessageID: z.string(),
  ServerID: z.number().int(),
  MessageStream: z.string(),
  Description: z.string(),
  Details: z.string().optional().default(''),
  Email: z.string().email(),
  From: z.string(),
  BouncedAt: z.string(), // ISO 8601
  DumpAvailable: z.boolean(),
  Inactive: z.boolean(),
  CanActivate: z.boolean(),
  Subject: z.string().optional().default(''),
});

export type PostmarkBounceWebhookPayload = z.infer<typeof PostmarkBounceWebhookPayloadSchema>;

export const PostmarkComplaintWebhookPayloadSchema = z.object({
  RecordType: z.literal('SpamComplaint'),
  ID: z.number().int(),
  Type: z.literal('SpamComplaint'),
  TypeCode: z.number().int(),
  Name: z.string(),
  Tag: z.string().optional().default(''),
  MessageID: z.string(),
  ServerID: z.number().int(),
  MessageStream: z.string(),
  Description: z.string().optional().default(''),
  Details: z.string().optional().default(''),
  Email: z.string().email(),
  From: z.string(),
  BouncedAt: z.string(),
  DumpAvailable: z.boolean(),
  Inactive: z.boolean(),
  CanActivate: z.boolean(),
  Subject: z.string().optional().default(''),
});

export type PostmarkComplaintWebhookPayload = z.infer<typeof PostmarkComplaintWebhookPayloadSchema>;

// Some Postmark deployments send SpamComplaint with `RecordType: 'SpamComplaint'`;
// older docs show `RecordType: 'Complaint'`. Accept both at the union level.
export const PostmarkWebhookPayloadSchema = z.discriminatedUnion('RecordType', [
  PostmarkBounceWebhookPayloadSchema,
  PostmarkComplaintWebhookPayloadSchema,
]);

export type PostmarkWebhookPayload = z.infer<typeof PostmarkWebhookPayloadSchema>;

// ─── Render pipeline (Decision D2 + D11) ─────────────────────────────────────

export interface EmailRenderResult {
  readonly subject: string;
  readonly html: string;
  /** Auto-generated from HTML via html-to-text (Decision D11). Always present. */
  readonly text: string;
}

export const EMAIL_TEMPLATE_NAMES = [
  'booking_confirmation',
  'payment_receipt',
  'password_reset',
  'otp_backup',
  'monthly_digest',
  'welcome',
  'account_deletion',
  'complaint_alert',
  'email_health_summary',
  'test_placeholder',
  // Data-driven branded letter — rendered from GenericEmailData (see
  // @bravemove/email's generic-data.ts). Used by apps/api's email-branding
  // composer to route the legacy plain-text bodies through the F28 shell.
  'generic',
] as const;

export type EmailTemplateName = (typeof EMAIL_TEMPLATE_NAMES)[number];

export const EmailTemplateNameSchema = z.enum(EMAIL_TEMPLATE_NAMES);

/**
 * Per-template metadata consumed by the EmailChannel orchestrator routing
 * logic + the unsubscribe-header injector. Single source of truth for
 * required-vs-opt-outable decisions (Decision D5).
 *
 * The actual map lives in @bravemove/email's template-registry; this type ensures
 * EmailChannel + tests + the admin dry-run UI share the same shape.
 */
export interface EmailTemplateMetadata {
  readonly name: EmailTemplateName;
  readonly defaultSubjectKey: string;
  readonly required: boolean;
  /** Only meaningful when `required = false`. */
  readonly optOutable: boolean;
  /** D5 — booking_confirmation suppresses email if matching FCM push was read within 10 min. */
  readonly suppressIfPushAcked: boolean;
  /** Which Postmark Server the template sends from. */
  readonly stream: 'transactional' | 'broadcast';
  /** F26 category for preference-matrix lookup. */
  readonly category:
    | 'booking'
    | 'payment'
    | 'commission'
    | 'system'
    | 'team'
    | 'promotion'
    | 'trainer'
    | 'monthly_digest'
    | 'newsletter';
}

// ─── Error codes (used across EmailChannel + routes + webhook handler) ──────

export const EMAIL_ERROR_CODES = [
  'INVALID_WEBHOOK_SIGNATURE',
  'POSTMARK_API_ERROR',
  'INVALID_UNSUBSCRIBE_TOKEN',
  'UNSUBSCRIBE_TOKEN_EXPIRED',
  'INACTIVE_RECIPIENT',
  'TEMPLATE_NOT_FOUND',
  'TEMPLATE_RENDER_FAILED',
  'SUPPRESSED_ADDRESS',
  'POSTMARK_RATE_LIMITED',
  'EMAIL_QUOTA_EXCEEDED',
] as const;

export type EmailErrorCode = (typeof EMAIL_ERROR_CODES)[number];

// ─── Admin email-suppressions CRUD inputs ───────────────────────────────────

export const AddEmailSuppressionInputSchema = z.object({
  emailAddress: z.string().email().toLowerCase().max(320),
  reason: z.string().min(1).max(500),
  notes: z.string().max(1000).optional(),
});

export type AddEmailSuppressionInput = z.infer<typeof AddEmailSuppressionInputSchema>;

export const RemoveEmailSuppressionInputSchema = z.object({
  reason: z.string().min(10).max(500),
});

export type RemoveEmailSuppressionInput = z.infer<typeof RemoveEmailSuppressionInputSchema>;

export const ListEmailSuppressionsQuerySchema = z.object({
  source: EmailSuppressionSourceSchema.optional(),
  q: z.string().max(320).optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  // Page-numbered pagination (admin table). When `page` is present the service
  // uses OFFSET pagination + a filtered COUNT and returns `pagination` meta.
  page: z.coerce.number().int().min(1).optional(),
  perPage: z.coerce.number().int().min(1).max(100).optional(),
});

export type ListEmailSuppressionsQuery = z.infer<typeof ListEmailSuppressionsQuerySchema>;

// ─── Unsubscribe-feedback POST (Decision D17) ───────────────────────────────

export const UnsubscribeFeedbackInputSchema = z.object({
  feedback: z.string().min(1).max(1000),
});

export type UnsubscribeFeedbackInput = z.infer<typeof UnsubscribeFeedbackInputSchema>;
